import { FC, useLayoutEffect, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { shouldShowLifeEvent } from 'enrollment/utils/LifeEventUtils/lifeEventVisibility';
import { ROUTES } from 'theSource/app/routes';
import { ListItemInfo } from 'theSource/components';
import { Entries } from 'theSource/types';
import { isValidHttpsUrl } from 'theSource/utils';
import './scss/MainNavigation.css';
import { LogoutButton } from 'theSource/components/LogoutButton';
import { useContentSettingsQuery, useConfigurationQuery, useEnrollmentContextQuery } from 'theSource/queries';
import { selectIsAdmin, selectLumosEnabled } from 'theSource/features';
import {
  setEnrollmentContext,
  selectPreviousEnrollmentContext,
} from 'theSource/features/Home/components/EnrollmentBanner/services';
import { NavHeader } from '../NavHeader';
import { Language } from '../Language';
import { AccountLinksGroup } from '../AccountLinksGroup';
import { ResetEnrollmentModal } from './ResetEnrollmentModal';
import { MyBenefitsNavItems } from './components';

declare const bootstrap: any;
export interface MainNavigationProps {
  isOpen: boolean;
  setIsNavigationOpen: (value: boolean) => void;
}

export interface ResetEnrollmentPlanYearProps {
  plan_year_id?: number;
  clear_coverages?: boolean;
}

export const MainNavigation: FC<MainNavigationProps> = ({
  isOpen,
  setIsNavigationOpen,
}) => {
  const { t } = useTranslation('navigation.additional');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAdmin = useSelector(selectIsAdmin);
  const previousEnrollmentContextType = useSelector(
    selectPreviousEnrollmentContext
  );
  const lumosEnabled = useSelector(selectLumosEnabled);

  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

  const { contentSettings } = useContentSettingsQuery({
    enabled: true,
  });

  const allowEarlyCheckout = isAdmin && contentSettings?.allow_early_checkout;
  const adminHomePageLink =
    isAdmin && contentSettings ? contentSettings.subscriber_admin_url : '';

  const proceedToCheckoutRoute: ListItemInfo = {
    name: 'navigation.additional:admin.proceedToCheckout',
    path: '/enrollment/review?earlyCheckout=true',
    handleClick: () => {
      dispatch(setEnrollmentContext(previousEnrollmentContextType));
      navigate('/enrollment/review?earlyCheckout=true'); //same review page view but different confirm api
      document.documentElement.classList.remove('main-nav-open');
    },
  };

  const adminHomePage: ListItemInfo = {
    name: 'navigation.additional:admin.home',
    path: adminHomePageLink,
    handleClick: () => {
      dispatch(setEnrollmentContext(''));
      if (adminHomePageLink.startsWith('http')) {
        window.location.href = adminHomePageLink; // External navigation
      }
    },
  };

  const resetEnrollment: ListItemInfo = {
    name: 'navigation.additional:admin.resetEnrollment',
    path: '',
    handleClick: () => {
      const dialog = new bootstrap.Modal(
        document.getElementById('reset-enrollment-modal')
      );
      dialog.show();
    },
  };

  let adminItems: ListItemInfo[] = [];

  if (isAdmin) {
    adminItems = isValidHttpsUrl(adminHomePageLink) ? [adminHomePage] : [];
    adminItems = [...adminItems, resetEnrollment];
    if (allowEarlyCheckout && previousEnrollmentContextType !== '') {
      adminItems = [...adminItems, proceedToCheckoutRoute];
    }
  }

  const location = useLocation();
  const navRoutes = ROUTES.additional;

  const finalNavRoutes = {
    home: '',
    profile: navRoutes['profile'],
    ...MyBenefitsNavItems(lumosEnabled),
    documents: navRoutes['documents'],
    company: navRoutes['company'],
  };

  const [isActive, setIsActive] = useState(isOpen);
  const orbitNavRef = useRef(null);
  const orbitNavParent = orbitNavRef.current as unknown as HTMLElement;

  const overlayRef = useRef(null);
  const closeNavButton = useRef<HTMLButtonElement>(null);

  const { configuration } = useConfigurationQuery();
  const { enrollmentContext } = useEnrollmentContextQuery({
    enabled: Boolean(configuration?.apiHost),
    apiHost: String(configuration?.apiHost),
  });
  
  const showLifeEvent = shouldShowLifeEvent(enrollmentContext);

  useLayoutEffect(() => {
    setIsActive(isOpen);
    if (isOpen) {
      orbitNavParent?.classList.remove('d-none');
      closeNavButton.current?.focus();
    }
  }, [isOpen, orbitNavParent]);

  useEffect(() => {
    // Used to lock the body when the navigation is open.
    const htmlElement = document.documentElement;
    if (isOpen) {
      htmlElement.classList.add('main-nav-open');
    } else {
      htmlElement.classList.remove('main-nav-open');
    }

    // overlay
    const overlay = overlayRef.current as unknown as HTMLElement;
    overlay.addEventListener('transitionend', onTransitionEnd);

    return () => {
      overlay.removeEventListener('transitionend', onTransitionEnd);
    };
    // eslint-disable-next-line
  }, [isOpen]);

  const onTransitionEnd = () => {
    if (!isOpen) {
      orbitNavParent?.classList.add('d-none');
    }
    const overlay = overlayRef.current as unknown as HTMLElement;
    overlay.removeEventListener('transitionend', onTransitionEnd);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsNavigationOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [setIsNavigationOpen]);

  // Added useEffect to handle anchor clicks
  useEffect(() => {
    const handleAnchorClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (target.innerText === t('admin.resetEnrollment')) {
        return;
      }

      // target seeks any <a> and<button> with class 'main-link' in the navigation panel
      if (
        target.tagName === 'A' ||
        target.closest('a') ||
        target.classList.contains('main-link')
      ) {
        setIsNavigationOpen(false);
      }
    };

    document.addEventListener('click', handleAnchorClick);

    return () => {
      document.removeEventListener('click', handleAnchorClick);
    };
  });

  // Focus trap to keep focus within the navigation panel
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const focusableElements = orbitNavParent.querySelectorAll(
      'a, button, textarea, input, select'
    );
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[
      focusableElements.length - 1
    ] as HTMLElement;

    const handleFocusTrap = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') {
        return;
      }

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          event.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          event.preventDefault();
        }
      }
    };

    document.addEventListener('keydown', handleFocusTrap);

    return () => {
      document.removeEventListener('keydown', handleFocusTrap);
    };
  }, [isOpen, orbitNavParent]);

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Navigation Entries
  const navRoutesEntries = Object.entries(finalNavRoutes) as Entries<
    typeof ROUTES.additional
  >;

  const adminRouteEntry = ['admin', adminItems] as [string, ListItemInfo[]];

  return (
    <>
      <section
        ref={orbitNavRef}
        className="main-nav d-none"
        role="presentation"
      >
        <div
          ref={overlayRef}
          className={`main-nav-overlay ${isActive ? 'active' : ''}`}
          aria-hidden="true"
          onTransitionEnd={onTransitionEnd}
        />
        <nav
          className={`main-nav-parent d-flex flex-column ${isActive ? 'active' : ''}`}
          tabIndex={-1}
        >
          <header className="nav-header" role="presentation">
            <div className="mb-3">
              <button
                ref={closeNavButton}
                id="closeMainNavigation"
                onClick={() => {
                  setIsNavigationOpen(false);
                  const mainNavTriggerButton =
                    document.getElementById('main-nav-trigger');
                  mainNavTriggerButton?.focus();
                }}
              >
                <i
                  className="ph ph-x"
                  aria-hidden="true"
                  role="presentation"
                ></i>
                <span className="sr-only">{t('common:close')}</span>
              </button>
            </div>
            <h1>{t('common:menu')}</h1>
          </header>
          <div className="nav-body" role="presentation">
            <NavHeader
              navRoutesEntries={navRoutesEntries}
              selectedLocation={location.pathname}
              planYearContext={location.search}
            />
            {showLifeEvent && (
              <div className="main-nav-section">
              <div
                className="main-nav-link"
                onClick={() => {
                  navigate('/life-event');
                  setIsNavigationOpen(false);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    navigate('/life-event');
                    setIsNavigationOpen(false);
                  }
                }}
              >
                <i className="ph ph-star" aria-hidden="true" />
                <span>{t('reportLifeEvent')}</span>
              </div>
            </div>            
            )}
            {isAdmin && (
              <NavHeader
                navRoutesEntries={[adminRouteEntry]}
                selectedLocation={location.pathname}
                planYearContext={location.search}
              />
            )}
            {viewportWidth < 1200 && (
              <>
                <AccountLinksGroup />
                <div className="mt-0 pb-3">
                  <Language />
                </div>
                <div className="my-3 d-lg-none">
                  <LogoutButton dataTestId="log-out-button-desktop" />
                </div>
              </>
            )}
          </div>
        </nav>
      </section>
      {isOpen && isAdmin && <ResetEnrollmentModal />}
    </>
  );
};
